import { PROPOSAL_DECISIONS } from "@/features/catalog_case_review/shared/constants/caseReviewDecisions";
import type { ProposalDecision } from "@/features/catalog_case_review/shared/model/agentProposal";

/** The single user-facing name. Internal ids (`page-helper`, `pa-*`) never change with it. */
export const THERAGENIE_LABEL = "Theragenie";

export const DOCK_LABELS = {
  open: `Ask ${THERAGENIE_LABEL}`,
  close: `Close ${THERAGENIE_LABEL}`,
  title: THERAGENIE_LABEL,
  description: "Ask about this screen or the case in front of you. Answers are machine-suggested and must be verified before use.",
  loading: "Opening your conversation…",
  emptyTitle: `Hi, I'm ${THERAGENIE_LABEL}.`,
  // The verification notice is in `description`, under the title; the welcome card does not repeat it.
  emptyDescription: "Ask about the screen you're on, or about this case.",
  you: "You",
  thinking: "Thinking…",
  noText: `${THERAGENIE_LABEL} returned no text for that question.`,
  question: "Your question",
  meddraRequest: "Find a MedDRA code for the reported term in this record.",
  hint: "Enter sends · Shift+Enter for a new line",
  send: "Ask",
  sending: "Thinking…",
  stop: "Stop",
  cancelledNote: "Stopped. What arrived before you stopped it is kept above.",
  runCapNote: `${THERAGENIE_LABEL} stopped after too many steps in a row. Ask again to continue.`,
  unnamedTool: "Using a tool",
  historyUnavailable: "Your earlier conversation could not be loaded. You can still ask a question — this starts a new conversation.",
  proposalsTitle: "Proposed changes",
  proposalsPending: "Accept or reject each change here or on the form. Nothing is saved until you accept and press Save.",
  proposalsDecided: "All decided. Press Save on the form to keep the accepted values.",
  workingThinking: "Thinking",
  workingPrefix: "Theragenie is working",
  activityLabel: "What Theragenie did for this question",
  proposalClosed: "Closed — the form was saved, cancelled or reloaded",
  proposalAccept: "Accept",
  proposalReject: "Reject",
  proposalGoToField: "Go to field",
  proposalUndo: "Undo",
  proposalRedo: "Redo",
  proposalEmpty: "(empty)",
  /** The chip on the composer while a field action's prompt waits to be sent. */
  fieldActionChip: (actionLabel: string, fieldLabel: string) => `${actionLabel} — ${fieldLabel}`,
  removeChip: (chip: string) => `Remove ${chip}`,
  clearChat: "Clear chat",
  clearing: "Clearing…",
  clearConfirm: `Clear this conversation? ${THERAGENIE_LABEL} will forget it. Proposals already on the form are not affected.`,
  clearConfirmAction: "Clear",
  clearCancel: "Cancel",
  clearFailed: "The conversation could not be cleared. It is unchanged — try again.",
  clearRunInProgress: `${THERAGENIE_LABEL} is still answering, perhaps in another tab. Wait for it to finish, then try again.`,
} as const;

/** What the card says about a proposal, keyed by its decision. */
export const PROPOSAL_STATE_LABELS: Record<ProposalDecision, string> = {
  [PROPOSAL_DECISIONS.pending]: "Pending",
  [PROPOSAL_DECISIONS.accepted]: "Accepted",
  [PROPOSAL_DECISIONS.rejected]: "Rejected — previous value restored",
  [PROPOSAL_DECISIONS.superseded]: "Replaced by a newer proposal",
  [PROPOSAL_DECISIONS.undone]: "Undone — previous value restored",
};

export const RUN_ERROR_LABELS = {
  unreachable: `${THERAGENIE_LABEL} could not be reached. Check your connection and try again — if it keeps happening, the service may be down and your administrator can check the server log.`,
  expiredSession: "Your session has expired. Sign in again to carry on — your conversation is saved.",
  unavailable: `${THERAGENIE_LABEL} is not available in this deployment. Nothing is wrong with your account — the agent has no model configured here.`,
} as const;

/**
 * What the tool trail calls each tool. The keys are the Java tools' `NAME` constants
 * (`case-db-service` `pageagent/tools/*Tool.java`, pinned by `pageAgentLabels.test.ts`) and the
 * frontend tools' declared names; the reader never sees a raw name.
 */
export const TOOL_LABELS: Record<string, string> = {
  get_case_summary: "Reading the case summary",
  list_sections: "Listing the case's sections",
  get_section_values: "Reading a section's values",
  list_records: "Listing a section's records",
  get_field_state: "Reading a field's review state",
  search_meddra: "Looking up MedDRA terms",
  recommendWhoDrug: "Preparing a WHODrug recommendation",
  search_whodrug: "Looking up WHODrug entries",
  get_source_document_text: "Reading the source document",
  search_cases: "Searching cases",
  draft_narrative: "Drafting a narrative",
  navigateTo: "Opening a screen",
  goToField: "Going to a field",
  highlightSource: "Highlighting the source document",
  openPanel: "Opening a panel",
  closePanel: "Closing a panel",
  proposeFieldValue: "Proposing a field value",
  setFilter: "Setting a filter",
};

/**
 * The trail label for a tool. A tool nobody has labelled yet degrades to a fixed reader-facing
 * string rather than throwing inside the stream subscriber, which would end the run; the test that
 * pins the map against the backend names is where the omission fails loudly.
 */
export function toolLabel(name: string): string {
  const label = TOOL_LABELS[name];
  if (label === undefined) {
    console.error(`No trail label is defined for tool ${name}`);
    return DOCK_LABELS.unnamedTool;
  }
  return label;
}
